<script>
import { timeDifference, formatDatetimeForGCApi, timestampToFormattedLocalTime } from './../helpers/datetime.js';
import { projections } from './../stores.js'
import Temperature from './forecast/Temperature.svelte'
import WindSpeed from './forecast/WindSpeed.svelte'
import WindGusts from './forecast/WindGusts.svelte'
import WindDirection from './forecast/WindDirection.svelte'
import Coverage from './forecast/Coverage.svelte'
import Humidity from './forecast/Humidity.svelte'
import RainAccumulation from './forecast/RainAccumulation.svelte'
import SnowAccumulation from './forecast/SnowAccumulation.svelte'

export let time;
export let bbox = "46.479820,-73.076197,46.574068,-72.829004"; // 1.3.0 long/lat EPSG 4326 coordinates e.g.:http://bboxfinder.com/#46.437857,-73.072128,46.570663,-72.745285

function retrieveWindDirection(time) {
  return retrieveProjection(time, "GDPS.ETA_WD")
}

function retrieveGusts(time) {
  return retrieveProjection(time, "GDPS.ETA_WGE")
}

function retrieveWindSpeed(time) {
  return retrieveProjection(time, "GDPS.ETA_WSPD")
}

function retrieveTemperature(time) {
  return retrieveProjection(time, "GDPS.ETA_TT")
}

function retrieveCloudCoverage(time) {
  return retrieveProjection(time, "GDPS.ETA_NT")
}

// kg/kg
function retrieveHumidity(time) {
  return retrieveProjection(time, "GDPS.ETA_HR")
}

// [kg/(m^2)]
function retrievePrecipitationAccumulation(time) {
  return retrieveProjection(time, "GDPS.ETA_PR")
}

// [kg/(m^2)]
function retrieveRainAccumulation(time) {
  return retrieveProjection(time, "GDPS.ETA_RN")
}

// [kg/(m^2)]
function retrieveSnowAccumulation(time) {
  return retrieveProjection(time, "GDPS.ETA_SN")
}

// m
function retrieveSnowThickness(time) {
  return retrieveProjection(time, "GDPS.ETA_SD")
}

// [kg/(m^2 s)]
function retrieveInstaPrecipitation(time) {
  return retrieveProjection(time, "GDPS.ETA_RT")
}

function retrieveProjection(time, layer) {
  try {
    return retrieveFromCache(time, layer)
  } catch (err) {
    return retrieveFromApi(time, layer);
  }
}

function retrieveFromApi(time, layer) {
  return new Promise(fulfil => {
    var url = new URL("https://geo.weather.gc.ca/geomet"),
      params = {
        lang: "E",
        service: "WMS",
        request: "GetFeatureInfo",
        bbox: bbox,
        version: "1.3.0",
        crs: "EPSG:4326",
        layers: layer,
        query_layers: layer,
        width: 10,
        height: 10,
        i: 5,
        j: 5
      };
    
    if (time)
      params['time'] = formatDatetimeForGCApi(time);

    Object.keys(params).forEach(key =>
      url.searchParams.append(key, params[key])
    );

    var request = new Request(url);
    var headers = new Headers();

    var options = {
      method: "GET",
      headers: headers,
      mode: "cors",
      cache: "default"
    };
    fetch(request, options).then(response => {
      response.text().then(function(text) {
        if (text.indexOf("value_0") === -1) {
          cacheResult(time, layer, null);
          return fulfil(null);
        }

        var values = text.split("\n");
        for (var i = 0; i < values.length; i++) {
          if (!values[i].includes("value_0")) 
            continue;

          let value = parseFloat(values[i].match(/'([^']+)'/)[1]).toFixed(2);
          cacheResult(time, layer, value);

          return fulfil(value);
        }
      });
    });
  });
}

function createCacheKey(layer, time) {
  return layer + "/" + formatDatetimeForGCApi(time);
}

function retrieveFromCache(time, layer) {
  if (typeof($projections[createCacheKey(layer, time)]) === "undefined") {
    throw "Value not found in cache"
  }
    
  return $projections[createCacheKey(layer, time)]
}

function cacheResult(time, layer, value) {
  if (time == "" || !time)
    return
  let p = {
    [createCacheKey(layer, time)]: value
  }
  if (!$projections) {
    projections.set(p)
    return;
  }
  let merge = Object.assign($projections, p)
  projections.set(merge);
}
</script>

<style>
  .prediction {
    width: 20%;
    text-align: center;
    margin-bottom: 25px;
  }
  .prediction p.loading {
    display: none;
  }
  .prediction p.loading:first-child {
    display: block;
  }
 .prediction .wind {
    margin-bottom: 12px;
  }
  .prediction .muted {
    color: #666;
  }
  @media only screen and (max-width: 600px) {
    .prediction {
      width: 100%;
      display: block;
    }
  }
</style>

<div class="prediction">
    <h5>{#if time}{timestampToFormattedLocalTime(time)}{:else}Current{/if}</h5>
    <h6>{#if time}+{timeDifference(time)}H{:else}+0H{/if}</h6>

  {#await retrieveCloudCoverage(time)}
    <p class="loading">Retrieving coverage... </p>
  {:then coverage}
    <Coverage coverage={parseInt(coverage)} />
  {/await}

  {#await retrieveTemperature(time) then temperature}
    <Temperature temperature={parseInt(temperature)} />
  {/await}

  <div class="wind">
    {#await retrieveWindSpeed(time) then windSpeed}
      <WindSpeed windSpeed={parseInt((windSpeed * 3.6))} />
    {/await}

    {#await retrieveWindDirection(time) then windDirection}
      <WindDirection windDirection={windDirection} />
    {/await}

    {#await retrieveGusts(time) then windGusts}
      {#if windGusts}<WindGusts windGusts={parseInt((windGusts * 3.6))} />{/if}
    {/await}
  </div>

  <div class="muted">
    {#await retrieveHumidity(time) then humidity}
      <Humidity humidity={humidity} />
    {/await}

    {#await retrieveRainAccumulation(time) then rain}
      {#if rain}<RainAccumulation rain={rain} />{/if}
    {/await}

    {#await retrieveSnowAccumulation(time) then snow}
      {#if snow}<SnowAccumulation snow={snow} />{/if}
    {/await}
  </div>
</div>